import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Percent } from "lucide-react";
import { motion } from "framer-motion";
import api from "../hooks/api";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import { API_URLS } from "../App";
import { Campaign } from "../model/Campaign";
import ListCampaignItems from "../components/Campaigns/ListCampaignItems";

const CampaignsListPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { setLoading, setError, setSuccess } = useLayoutContext();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [profileData, setProfileData] = useState<ProfileAndSettings | null>(
    null,
  );
  const [filter, setFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const loadProfile = async () => {
    try {
      const p = await profile();
      setProfileData(p);
    } catch {
      console.warn("Failed to load profile");
    }
  };

  const loadCampaigns = async () => {
    setLoading({ message: "A carregar campanhas..." });
    try {
      const response = await api.get(API_URLS.CAMPAIGNS);
      setCampaigns(response.data as Campaign[]);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar campanhas");
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    loadProfile();
    loadCampaigns();
  }, []);

  const canCreate = !!(profileData?.isAdmin() || profileData?.isMainAccount());
  const canEdit = !!(profileData?.isAdmin() || profileData?.isMainAccount());

  const filteredCampaigns = campaigns
    .filter((c) => filter === "all" || c.status === filter)
    .filter(
      (c) =>
        !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Percent className="w-7 h-7 text-blue-500" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                Campanhas Promocionais
              </h1>
              <p className="text-gray-500 mt-1">
                {canCreate
                  ? "Cria e gere campanhas promocionais para os teus clientes."
                  : "Visualiza as campanhas promocionais disponíveis."}
              </p>
            </div>
          </div>
          {canCreate && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/campaigns/new")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 shrink-0"
            >
              <Plus className="w-4 h-4" />
              Nova Campanha
            </motion.button>
          )}
        </div>

        {/* Content */}
        <div className="overflow-auto">
          {campaigns.length === 0 && !searchTerm && filter === "all" ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Percent className="w-16 h-16 mx-auto mb-4 text-gray-200" />
              <p className="text-lg font-medium text-gray-500">
                Nenhuma campanha encontrada.
              </p>
              {canCreate && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/campaigns/new")}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Criar primeira campanha
                </motion.button>
              )}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ListCampaignItems
                data={filteredCampaigns}
                filter={filter}
                onFilterChange={setFilter}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                canEdit={canEdit}
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignsListPage;
